import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';

export default function EmailTab({ stats, refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailBody, setBulkEmailBody] = useState('');
  const [emailTarget, setEmailTarget] = useState('all');
  const [emailHistory, setEmailHistory] = useState([]);

  const loadEmailHistory = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/email-history`);
      if (response.ok) {
        const data = await response.json();
        setEmailHistory(data.email_history || []);
      }
    } catch (error) {
      console.error('Error loading email history:', error);
    }
  }, [adminFetch, API_URL]);

  useEffect(() => { loadEmailHistory(); }, [loadEmailHistory, refreshTrigger]);

  const sendBulkEmail = async () => {
    if (!bulkEmailSubject.trim() || !bulkEmailBody.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/bulk-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: emailTarget, subject: bulkEmailSubject, body: bulkEmailBody })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Email queued for ${data.recipient_count} users`);
        setBulkEmailSubject('');
        setBulkEmailBody('');
        loadEmailHistory();
      } else {
        toast.error('Failed to send emails');
      }
    } catch (error) {
      toast.error('Error sending emails');
    }
  };

  const recipientCount = emailTarget === 'all' ? stats.totalUsers : emailTarget === 'free' ? stats.freeUsers : stats.paidUsers;

  return (
    <div data-testid="email-tab">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-400" />
            Bulk Email
          </CardTitle>
          <CardDescription className="text-slate-400">Send emails to your users</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300">Send To</Label>
            <div className="flex gap-3 mt-2">
              {['all', 'free', 'paid'].map(target => (
                <button
                  key={target}
                  onClick={() => setEmailTarget(target)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    emailTarget === target
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {target === 'all' ? 'All Users' : target === 'free' ? 'Free Users' : 'Paid Users'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-slate-300">Subject</Label>
            <Input
              value={bulkEmailSubject}
              onChange={(e) => setBulkEmailSubject(e.target.value)}
              placeholder="Email subject..."
              className="bg-slate-700 border-slate-600 text-white mt-1"
            />
          </div>

          <div>
            <Label className="text-slate-300">Message</Label>
            <textarea
              value={bulkEmailBody}
              onChange={(e) => setBulkEmailBody(e.target.value)}
              placeholder="Write your message..."
              rows={8}
              className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <Button onClick={sendBulkEmail} className="bg-orange-500 hover:bg-orange-400">
            <Mail className="h-4 w-4 mr-2" />
            Send Email to {recipientCount} users
          </Button>
        </CardContent>
      </Card>

      {/* Email History */}
      <Card className="bg-slate-800 border-slate-700 mt-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-400" />
            Email History
          </CardTitle>
          <CardDescription className="text-slate-400">Past email campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          {emailHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No email campaigns sent yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {emailHistory.map((email, idx) => (
                <div key={email.log_id || idx} className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{email.subject}</h4>
                      <p className="text-xs text-slate-400 mt-1">{new Date(email.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        email.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        email.status === 'partial' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>{email.status}</span>
                      <span className="text-xs text-slate-400 capitalize">To: {email.target} users</span>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-400">Sent: {email.sent_count}</span>
                    {email.failed_count > 0 && <span className="text-red-400">Failed: {email.failed_count}</span>}
                    <span className="text-slate-400">Total: {email.total_recipients}</span>
                  </div>
                  <p className="text-slate-300 text-sm mt-2 line-clamp-2">{email.body}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
